import {Component, OnDestroy, OnInit, Renderer2, ViewChild} from "@angular/core";
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterLink,
  RouterLinkActive
} from "@angular/router";
import moment from "moment";
import {filter, forkJoin, take} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {AuthenticationService} from "../common/authentication.service";
import {SharedEventsService} from "../common/shared-events-service";
import {SharedStateService} from "../common/shared-state.service";
import {ProductDTO, UserService} from "../common/user.service";
import {ProductService} from "../product/product.service";
import {NotificationService} from "./notification/notification.service";
import {SavePageUtil} from "../common/util/save-page-util";
import {ProgramService} from "../program/program.service";
import {PortfolioReviewService} from "../portfolio-review/portfolio-review.service";
import {RightsManagementService} from "../rights-management/rights-management.service";
import {Enabler, EnablerService} from "../enabler/enabler.service";
import {EnablerProjectIdNameDTO, EnablerProjectService} from "../enabler/enabler-project.service";
import {ReferentialService} from "../common/referential/referential.service";
import {NgbPopover} from "@ng-bootstrap/ng-bootstrap";
import {KeyValuePipe, NgClass, NgStyle, NgTemplateOutlet} from "@angular/common";
import {MatMenuTrigger} from "@angular/material/menu";
import {PopoverComponent} from "../common/popover/popover.component";
import {SafeHtmlPipe} from "../common/util/safe-html-pipe";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  imports: [NgbPopover, NgClass, RouterLink, MatMenuTrigger, PopoverComponent, RouterLinkActive, NgTemplateOutlet, NgStyle, KeyValuePipe, SafeHtmlPipe]
})
export class HeaderComponent implements OnInit, OnDestroy {
  userProducts: ProductDTO[] = [];
  selectedProductId: number;
  temporaryProduct: ProductDTO | null = null;
  temporaryEnablerProject: EnablerProjectIdNameDTO | null = null;
  selectedEnablerProjectId: number;
  isAllLoaded = true;
  loading: boolean = false;
  public username: string;

  canAddProduct: boolean = false;
  canAddDataProduct: boolean = false;
  canAddProgram: boolean = false;
  canAddEnablerProject: boolean = false;
  isAdmin: boolean = this.userService.getUserIsAdmin();
  isReaderProduct: boolean;
  isReaderDataProduct: boolean;
  isReaderEnablerProject: boolean;
  isWriterRightsManagement: boolean = false;
  programs: any[] = [];
  programMenuItems: any = {};
  programMenuOpened: boolean = false;
  enablerMenuItems: any;
  enablerMenuOpened: boolean = false;

  @ViewChild("notificationMenuRef") notificationMenuRef;
  seeMore: boolean;
  notifications: any;
  notificationsToDisplay: any;
  notificationDisplaySelected: number = 0;
  lastOpenedNotifications: number;
  notificationsNotOpenedCount: number = 0;

  portfolioReviewType: string;

  branchMenuOpened: boolean = false;
  domainMenuOpened: boolean = false;
  organizationMenuOpened: boolean = false;
  dataPlatformMenuOpened: boolean = false;
  monitoringMenuOpened: boolean = false;
  roadmapPreparationMenuOpened: boolean = false;
  portfolioMenuItems: any = {};
  monitoringMenuItems: any = {};
  roadmapPreparationMenuItems: any = {};
  hasRightsOnProgramRoadmapPreparationPage: boolean = false;
  hasRightsOnLBURoadmapPreparationPage: boolean = false;

  private outsideClickListener = () => {
  };

  constructor(
    public authenticationService: AuthenticationService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sharedEventsService: SharedEventsService,
    public sharedStateService: SharedStateService,
    private readonly notificationService: NotificationService,
    private readonly toastrService: ToastrService,
    private readonly productService: ProductService,
    private readonly programService: ProgramService,
    private readonly renderer: Renderer2,
    private readonly savePageUtil: SavePageUtil,
    private readonly portfolioReviewService: PortfolioReviewService,
    private readonly rightsManagementService: RightsManagementService,
    private readonly enablerService: EnablerService,
    private readonly enablerProjectService: EnablerProjectService,
    private readonly referentialService: ReferentialService
  ) {
  }

  ngOnInit(): void {
    if (this.authenticationService.userIsAuthenticated) {
      this.username = this.authenticationService.username;
      if (this.userHasRights()) {
        this.whenNavigationSettled(() => {
          this.loadUserProducts();
          this.loadPrograms();
          this.loadEnablers();
          this.loadPortfolios();
        });

        this.sharedEventsService.reloadHeader$.subscribe(() => {
          this.loadUserProducts();
          this.loadPrograms();
          this.loadEnablers();
          this.loadPortfolios();
        });

        this.sharedEventsService.selectedProduct$.subscribe((product) => {
          if (!this.userProducts.some((userProduct) => userProduct.id === product.id)) {
            this.temporaryProduct = product;
          }
          this.selectedProductId = product?.id || null;
        });

        this.sharedEventsService.selectedEnablerProject$.subscribe((enablerProject) => {
          this.selectedEnablerProjectId = enablerProject?.id || null;
          this.temporaryEnablerProject = enablerProject || null;
        });

        this.userService.getLastOpenedNotifications().subscribe((lastOpened) => {
          this.lastOpenedNotifications = lastOpened.result;
          this.userService.getNotifications().subscribe((notifications) => {
            this.notifications = notifications;
            this.notificationsToDisplay = notifications;
            this.notificationsNotOpenedCount = this.notifications.filter((notification) => notification.date > this.lastOpenedNotifications).length;
          });
        });

        this.route.firstChild?.paramMap.subscribe((paramMap) => {
          this.portfolioReviewType = paramMap.get("type")?.toUpperCase();
        });

        this.canAddProduct = this.rightsManagementService.hasRights(`Management - Add Product`, null, "write");
        this.canAddDataProduct = this.rightsManagementService.hasRights(`Management - Add Data Product`, null, "write");
        this.canAddProgram = this.rightsManagementService.hasRights(`Program Page`, null, "write");
        this.canAddEnablerProject = this.rightsManagementService.hasRights(`Management - Add Enabler Project`, null, "write");
        this.isReaderProduct = this.rightsManagementService.hasRights(`Product Page`, null, "read");
        this.isReaderDataProduct = this.rightsManagementService.hasRights(`Data Product Page`, null, "read");
        this.isReaderEnablerProject = this.rightsManagementService.hasRights(`Enabler Project Page`, null, "read");
        this.isWriterRightsManagement = this.rightsManagementService.hasRights(`Rights Management Page`, null, "write");
        this.hasRightsOnProgramRoadmapPreparationPage = this.rightsManagementService.hasRights("Program Roadmap Preparation Page", null, "read");
        this.hasRightsOnLBURoadmapPreparationPage = this.rightsManagementService.hasRights("LBU Roadmap Preparation Page", null, "read");

        // Initialize Roadmap Preparation menu items based on rights
        this.roadmapPreparationMenuItems = {};
        if (this.hasRightsOnLBURoadmapPreparationPage) {
          this.roadmapPreparationMenuItems["LBU"] = {};
        }
        if (this.hasRightsOnProgramRoadmapPreparationPage) {
          this.roadmapPreparationMenuItems["Program"] = {};
        }

        this.outsideClickListener = this.renderer.listen("window", "click", (e: any) => {
          if (!document.getElementById("branch-nav")?.contains(e.target)) {
            this.closeBranch();
          }
          if (!document.getElementById("domain-nav")?.contains(e.target)) {
            this.closeDomain();
          }
          if (!document.getElementById("organization-nav")?.contains(e.target)) {
            this.closeOrganization();
          }
          if (!document.getElementById("data-platform-nav")?.contains(e.target)) {
            this.closeDataPlatform();
          }
          if (!document.getElementById("monitoring-nav")?.contains(e.target)) {
            this.closeMonitoring();
          }
          if (!document.getElementById("roadmap-preparation-nav")?.contains(e.target)) {
            this.closeRoadmapPreparation();
          }
          if (!document.getElementById("program-nav")?.contains(e.target)) {
            this.closeProgram();
          }
          if (!document.getElementById("enabler-nav")?.contains(e.target)) {
            this.closeEnabler();
          }
        });
      }
    }
  }

  private whenNavigationSettled(callback: () => void) {
    if (this.router.navigated && !this.router.getCurrentNavigation()) {
      callback();
      return;
    }
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
        take(1)
      )
      .subscribe(callback);
  }

  addPortfoliosToMenu(portfoliosByType: any) {
    for (const type in portfoliosByType) {
      // Only include portfolios with read rights
      const portfolios = portfoliosByType[type]
        .filter((item: any) => !(item.hidden))
        .map((item: any) => ({
          ...item,
          children: item.children?.filter((child: any) =>
            this.rightsManagementService.hasRights(type == "Organization" ? `Portfolio - ${child.name} Page` : `Portfolio - ${type} Page`, child.name, "read")
          ) || []
        }))
        .filter((item: any) =>
          this.rightsManagementService.hasRights(type == "Organization" ? `Portfolio - ${item.name} Page` : `Portfolio - ${type} Page`, item.name, "read") ||
          (item.children && item.children.length > 0)
        );
      if (portfolios.length > 0) {
        this.portfolioMenuItems[type] = {subItems: {}};
        portfolios.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
        for (const item of portfolios) {
          this.portfolioMenuItems[type].subItems[item.name] = {};
          if (item.children?.length > 0) {
            item.children.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
            for (const child of item.children) {
              this.portfolioMenuItems[type].subItems[item.name].subItems ??= {};
              this.portfolioMenuItems[type].subItems[item.name].subItems[child.name] = {};
            }
          }
        }
      }
    }
  }

  savePage(type: "CT - Product" | "CT - Portfolio") {
    this.savePageUtil.savePage(type, (loading) => (this.loading = loading), this.temporaryProduct, this.userProducts, this.selectedProductId);
  }

  ngOnDestroy() {
    this.outsideClickListener();
  }

  loadUserProducts() {
    this.userService.getUserFavoriteProducts(this.authenticationService.email).subscribe(
      (favoriteProducts) => {
        this.userProducts = favoriteProducts;

        // Redirect to first product if no route active
        if (
          !this.isOnRoute("product") &&
          !this.isOnRoute("portfolio-review") &&
          !this.isOnRoute("search") &&
          !this.isOnRoute("help") &&
          !this.isOnRoute("monitoring") &&
          !this.isOnRoute("rights-management") &&
          !this.isOnRoute("lbu-roadmap-preparation") &&
          !this.isOnRoute("program") &&
          !this.isOnRoute("digital-line") &&
          !this.isOnRoute("enabler") &&
          !this.isOnRoute("enabler-project") &&
          !this.isOnRoute("admin-panel")
        ) {
          if (this.userProducts.length == 0) {
            this.router.navigate(["/search"]);
          } else {
            const firstProduct = this.userProducts[0];
            this.router.navigate(["/product", firstProduct.name, firstProduct.id]); // Redirect to first product
            this.selectedProductId = firstProduct.id;
          }
        } else {
          const productIdParam = this.route.snapshot.firstChild?.paramMap.get("id");
          this.selectedProductId = Number.parseInt(productIdParam, 10);

          if (!this.userProducts.some((userProduct) => userProduct.id === this.selectedProductId) && this.isOnRoute("product")) {
            this.productService.getBaseProductInfo(this.selectedProductId).subscribe((product) => {
              this.temporaryProduct = product;
            });
          }

          // Handle enabler project in header when reloading page
          if (this.isOnRoute("enabler-project")) {
            const enablerProjectIdParam = this.route.snapshot.firstChild?.paramMap.get("id");
            if (enablerProjectIdParam) {
              this.enablerProjectService.getBaseEnablerProjectInfo(Number.parseInt(enablerProjectIdParam, 10)).subscribe((enablerProject) => {
                this.temporaryEnablerProject = enablerProject;
                this.selectedEnablerProjectId = enablerProject?.id || null;
              });
            }
          }
        }
      },
      (error) => {
        console.error("Error when getting the products :", error);
      }
    );
  }

  loadPrograms() {
    this.programService.getAllProgramIdAndNamesAndFamily().subscribe(programs => {
      // Store raw programs in shared state (before rights filtering)
      this.sharedStateService.setAllPrograms(programs);

      // Only include programs with read rights
      this.programs = programs.filter(program =>
        this.rightsManagementService.hasRights(`Program Page`, program.name, "read") || this.rightsManagementService.hasRights(`Program Page - External Access`, program.name, "read")
      );
      this.programMenuItems = {};

      for (let program of this.programs) {
        if (program.family) {
          this.programMenuItems[program.family] ??= {subItems: {}};
          this.programMenuItems[program.family].subItems[program.name] = {id: program.id};
        } else {
          this.programMenuItems[program.name] = {id: program.id};
        }
      }
    });
  }

  loadEnablers() {
    this.enablerService.getEnablersGroupedByFamily().subscribe((data: { [key: string]: Enabler[] }) => {
      if (!this.rightsManagementService.hasRights("Enabler Page", null, "read")) {
        this.enablerMenuItems = null;
        return;
      }
      this.enablerMenuItems = {};
      for (const key in data) {
        // If the key matches the family of the first enabler, treat as family group
        if (data[key][0].family && data[key][0].family === key) {
          this.enablerMenuItems[key] = {subItems: {}};
          for (const enabler of data[key]) {
            this.enablerMenuItems[key].subItems[enabler.name] = {id: enabler.id};
          }
        } else {
          // Otherwise, it's a single enabler without a family
          this.enablerMenuItems[key] = {id: data[key][0].id};
        }
      }

      this.sharedStateService.setEnablersByFamily(data);
    });
  }

  loadPortfolios() {
    this.referentialService.getReferences("program-instances").subscribe((instances) => {
      this.sharedStateService.setProgramInstances(instances.map((instance) => instance.name));
    });
    forkJoin({
      domains: this.referentialService.getReferences("domains"),
      disciplines: this.referentialService.getReferences("disciplines")
    }).subscribe(({domains, disciplines}) => {
      this.sharedStateService.setDomainColors(domains.map((domain) => ({name: domain.name, color: domain.color})));
      this.sharedStateService.setDisciplineColors(disciplines.map((discipline) => ({
        name: discipline.name,
        domain: domains.find((domain) => domain.id === discipline.parentId).name,
        color: discipline.color
      })));
    });
    // Portfolio MENUS
    this.portfolioReviewService.getAllPortfoliosByType().subscribe((portfoliosByType: any) => {
      this.portfolioMenuItems = {};
      this.addPortfoliosToMenu(portfoliosByType);

      if (this.rightsManagementService.hasRights("Portfolio - Digital Line Page", null, "read")) {
        this.portfolioMenuItems["Organization"] ??= {subItems: {}};
        this.portfolioMenuItems["Organization"].subItems["Digital Line"] = {};
      }
    });

    let newMonitoringMenuItems = {};
    for (let key of ["TDF", "Digital Line"]) {
      if (this.rightsManagementService.hasRights(`Monitoring - ${key} Page`, null, "read") ||
        this.rightsManagementService.hasRights(`Monitoring - ${key} Page`, null, "write")) {
        newMonitoringMenuItems[key] = {};
      }
    }
    this.monitoringMenuItems = newMonitoringMenuItems;
  }

  onProductSelect(productId: number): void {
    this.selectedProductId = productId;
    this.temporaryProduct = null;
    this.selectedEnablerProjectId = null;
    this.temporaryEnablerProject = null;
  }

  onEnablerProjectSelect(enabler: any): void {
    this.selectedProductId = null;
    this.temporaryProduct = null;
    this.selectedEnablerProjectId = enabler.id;
    this.temporaryEnablerProject = enabler;
    this.enablerMenuOpened = false;
  }

  onProgramSelect(): void {
    this.selectedProductId = null;
    this.temporaryProduct = null;
    this.selectedEnablerProjectId = null;
    this.temporaryEnablerProject = null
    this.programMenuOpened = false;
  }

  enablerMenuClicked(): void {
    this.selectedProductId = null;
    this.temporaryProduct = null;
    this.selectedEnablerProjectId = null;
    this.temporaryEnablerProject = null;
    this.enablerMenuOpened = !this.enablerMenuOpened;
    this.branchMenuOpened = false;
    this.domainMenuOpened = false;
    this.organizationMenuOpened = false;
    this.programMenuOpened = false;
    this.monitoringMenuOpened = false;
    this.roadmapPreparationMenuOpened = false;
  }

  emptyTemporaryProductAndEnablerProject(): void {
    this.selectedProductId = null;
    this.temporaryProduct = null;
    this.selectedEnablerProjectId = null;
    this.temporaryEnablerProject = null;
  }

  isOnRoute(routeIncludes: string): boolean {
    const currentUrl = decodeURIComponent(this.router.url);
    return currentUrl.includes(routeIncludes);
  }

  getDateDiff(notificationDate: number) {
    let daysDiff = Math.floor(moment().diff(moment(notificationDate)) / 1000 / 60 / 60 / 24);
    return daysDiff == 0 ? "today" : daysDiff + "d";
  }

  updateNotificationsDisplayed(optionSelected: number) {
    this.notificationDisplaySelected = optionSelected;
    this.notificationsToDisplay = optionSelected == 1 ? this.notifications.filter((notif) => !notif.read) : this.notifications;
  }

  invertNotificationRead(notification: any) {
    this.notificationService.invertNotificationRead(notification.id).subscribe(
      () => {
        notification.read = !notification.read;
        this.notificationsToDisplay = this.notificationDisplaySelected == 1 ? this.notifications.filter((notif) => !notif.read) : this.notifications;
      },
      () => {
        this.toastrService.error("An error occurred.");
      }
    );
  }

  deleteNotification(notification: any) {
    this.notificationService.deleteNotification(notification.id).subscribe(
      () => {
        this.notifications.splice(this.notifications.indexOf(notification), 1);
      },
      () => {
        this.toastrService.error("An error occurred.");
      }
    );
  }

  notificationsOpened() {
    this.notificationService.notificationsOpened().subscribe(() => {
      this.lastOpenedNotifications = new Date().valueOf();
      this.notificationsNotOpenedCount = 0;
    });
  }

  goToNotification(notification) {
    this.notificationMenuRef.close();
    this.onProductSelect(notification.productId);
    this.notificationService.markAsRead(notification.id).subscribe(() => {
      notification.read = true;
    });

    if (notification.topic == "actionCreated" || notification.topic.startsWith("actionPlanReminder")) {
      this.router.navigate(["/product", notification.productName, notification.productId], {fragment: "action-plan"});
    } else if (notification.topic == "portfolioActionCreated" || notification.topic.startsWith("portfolioActionPlanReminder")) {
      this.router.navigate(["/portfolio-review/" + notification.actionPlan.portfolio.type + "/" + notification.actionPlan.portfolio.name]);
    } else if (notification.topic == "enablerActionCreated" || notification.topic.startsWith("enablerActionPlanReminder")) {
      this.router.navigate(["/enabler/" + notification.actionPlan.enablerProject.enabler.name + "/" + notification.actionPlan.enablerProject.enabler.id]);
    } else if (notification.topic == "programActionCreated" || notification.topic.startsWith("programActionPlanReminder")) {
      this.router.navigate(["/program/" + notification.actionPlan.program.name + "/" + notification.actionPlan.program.id]);
    } else if (notification.topic == "eventReminder" || notification.topic == "groupReminder") {
      this.router.navigate(["/product", notification.productName, notification.productId]);

      let fragment = "roadmap-" + (notification.topic.startsWith("event") ? "event" : "group");
      if (notification.topic.startsWith("event")) fragment += "-" + notification.roadmapEvent.id;
      else fragment += "-" + notification.roadmapGroup.id;

      this.router.navigate(["/product", notification.productName, notification.productId], {
        fragment: fragment,
      });
    } else if (notification.topic == "deploymentPlanChanges" || notification.topic == "deploymentPlanCancelled" || notification.topic == "deploymentPlanReadyForSiteDatesChanges" || notification.topic == "deploymentPlan1stUsageDatesChanges"
      || notification.topic == "roadmapDeploymentReadyForSiteDateChanged" || notification.topic == "roadmapDeployment1stUsageDateChanged" || notification.topic == "roadmapDeploymentAddedToDeploymentPlan"
      || notification.topic == "roadmapDeploymentCancelled" || notification.topic == "roadmapDeploymentCreated") {
      this.router.navigate(["/product", notification.productName, notification.productId], {
        fragment: "roadmap",
      });
    } else if (notification.topic.startsWith("lbuRoadmap")) {
      let fragment = "lbuRoadmap-" + notification.lbu.id;
      this.router.navigate(["/lbu-roadmap-preparation"], {
        fragment: fragment,
      });
    } else if (notification.topic.startsWith("programInvalidated") || notification.topic.startsWith("lbuValidatedModified")) {
      this.router.navigate(["/program-roadmap-preparation"]);
    }
  }

  seeMoreClicked() {
    this.seeMore = true;
    this.updateNotificationsDisplayed(this.notificationDisplaySelected);
  }

  itemClicked(menuName: string, item: any, level: number) {
    if (item.value.subItems) {
      item.value.open = !item.value.open;
    }

    if (item.value.open && level == 0) {
      // Close the other level 0 items
      if (menuName == "portfolio") {
        Object.keys(this.portfolioMenuItems).forEach((menuItemKey) => {
          if (menuItemKey != item.key) this.portfolioMenuItems[menuItemKey].open = false;
        });
      } else {
        Object.keys(this.monitoringMenuItems).forEach((menuItemKey) => {
          if (menuItemKey != item.key) this.monitoringMenuItems[menuItemKey].open = false;
        });
      }
    }
  }

  selectMenuItem(menuName: string, parentPath: string, name: string, level: number, item: any) {
    if (menuName == "portfolio") {
      let path: Array<string> = parentPath
        .split("/")
        .filter((path) => path != "")
        .concat(name)
        .map((path) => encodeURIComponent(path));

      if (path[0] == "Domain" && level > 0) {
        // Discipline has been selected
        path[0] = "Discipline";
        path.splice(1, 1);
      }

      if (path[0] == "Organization" && decodeURIComponent(path[1]) == "Digital Line") {
        this.router.navigate([`/digital-line/`]);
      } else {
        this.router.navigate([`/portfolio-review/`].concat(path));
      }
      this.closeAllPortfolioMenus();
    } else if (menuName === "program") {
      this.router.navigate([`/program/`, item.value.id]);
      this.closeProgram();
    } else if (menuName === "enabler") {
      this.router.navigate([`/enabler/${name}/${item.value.id}`]);
      this.enablerMenuOpened = false;
    } else if (menuName === "roadmapPreparation") {
      if (name === "Program") {
        this.router.navigate(["/program-roadmap-preparation"]);
      } else if (name === "LBU") {
        this.router.navigate(["/lbu-roadmap-preparation"]);
      }
      this.closeRoadmapPreparation();
      this.emptyTemporaryProductAndEnablerProject();
    } else {
      this.router.navigate([`/monitoring/${name}`]);
      this.closeMonitoring();
    }

    this.selectedProductId = null;
    this.temporaryProduct = null;
  }

  closeAllPortfolioMenus() {
    this.branchMenuOpened = false;
    this.domainMenuOpened = false;
    this.organizationMenuOpened = false;
    this.dataPlatformMenuOpened = false;
    this.enablerMenuOpened = false;
    this.closeBranch();
    this.closeDomain();
    this.closeOrganization();
    this.closeDataPlatform();
    this.closeProgram();
  }

  closeBranch() {
    this.branchMenuOpened = false;
    if (this.portfolioMenuItems.Branch) {
      for (const subItem of Object.keys(this.portfolioMenuItems.Branch.subItems)) {
        this.portfolioMenuItems.Branch.subItems[subItem].open = false;
      }
    }
  }

  closeDomain() {
    this.domainMenuOpened = false;
    if (this.portfolioMenuItems.Domain) {
      for (const subItem of Object.keys(this.portfolioMenuItems.Domain.subItems)) {
        this.portfolioMenuItems.Domain.subItems[subItem].open = false;
      }
    }
  }

  closeOrganization() {
    this.organizationMenuOpened = false;
    if (this.portfolioMenuItems.Organization) {
      for (const subItem of Object.keys(this.portfolioMenuItems.Organization.subItems)) {
        this.portfolioMenuItems.Organization.subItems[subItem].open = false;
      }
    }
  }

  closeDataPlatform() {
    this.dataPlatformMenuOpened = false;
    if (this.portfolioMenuItems["Data Platform"]) {
      for (const subItem of Object.keys(this.portfolioMenuItems["Data Platform"].subItems)) {
        this.portfolioMenuItems["Data Platform"].subItems[subItem].open = false;
      }
    }
  }

  closeProgram() {
    this.programMenuOpened = false;
    for (const menuItemKey of Object.keys(this.programMenuItems)) {
      this.programMenuItems[menuItemKey].open = false;
      if (this.programMenuItems[menuItemKey].subItems) {
        for (const subItem of Object.keys(this.programMenuItems[menuItemKey].subItems)) {
          this.programMenuItems[menuItemKey].subItems[subItem].open = false;
        }
      }
    }
  }

  closeMonitoring() {
    this.monitoringMenuOpened = false;
    for (const menuItemKey of Object.keys(this.monitoringMenuItems)) {
      this.monitoringMenuItems[menuItemKey].open = false;
      if (this.monitoringMenuItems[menuItemKey].subItems) {
        for (const subItem of Object.keys(this.monitoringMenuItems[menuItemKey].subItems)) {
          this.monitoringMenuItems[menuItemKey].subItems[subItem].open = false;
        }
      }
    }
  }

  closeEnabler() {
    this.enablerMenuOpened = false;
    if (this.portfolioMenuItems.Enabler) {
      for (const subItem of Object.keys(this.portfolioMenuItems.Enabler.subItems)) {
        this.portfolioMenuItems.Enabler.subItems[subItem].open = false;
      }
    }
  }

  monitoringMenuClicked() {
    if (Object.keys(this.monitoringMenuItems).length > 1) {
      this.monitoringMenuOpened = !this.monitoringMenuOpened;
      this.enablerMenuOpened = false;
      this.closeAllPortfolioMenus();
      this.closeProgram();
      if (!this.monitoringMenuOpened) this.closeMonitoring();
    } else {
      this.selectMenuItem("monitoring", null, Object.keys(this.monitoringMenuItems)[0], null, null);
    }
  }

  roadmapPreparationMenuClicked() {
    if (Object.keys(this.roadmapPreparationMenuItems).length > 1) {
      this.roadmapPreparationMenuOpened = !this.roadmapPreparationMenuOpened;
      this.programMenuOpened = false;
      this.enablerMenuOpened = false;
      this.closeAllPortfolioMenus();
      this.closeMonitoring();
      if (!this.roadmapPreparationMenuOpened) this.closeRoadmapPreparation();
    } else {
      // Navigate directly if only one option
      const onlyOption = Object.keys(this.roadmapPreparationMenuItems)[0];
      if (onlyOption === "Program") {
        this.router.navigate(["/program-roadmap-preparation"]);
      } else if (onlyOption === "LBU") {
        this.router.navigate(["/lbu-roadmap-preparation"]);
      }
      this.emptyTemporaryProductAndEnablerProject();
    }
  }

  closeRoadmapPreparation() {
    this.roadmapPreparationMenuOpened = false;
    for (const menuItemKey of Object.keys(this.roadmapPreparationMenuItems)) {
      this.roadmapPreparationMenuItems[menuItemKey].open = false;
    }
  }

  anyMonitoringAvailable(): boolean {
    return Object.keys(this.monitoringMenuItems).length > 0;
  }

  anyRoadmapPreparationAvailable(): boolean {
    return Object.keys(this.roadmapPreparationMenuItems).length > 0;
  }

  getNumberOfSubItems(item: any): number {
    if (item?.subItems) {
      return Object.keys(item.subItems).length;
    }
    return 0;
  }

  hasMoreThanOneSubItem(item: object): boolean {
    return Object.keys(item).length > 1;
  }

  hasAtLeastOnePortfolio(item: object): boolean {
    return item ? Object.keys(item).length > 0 : false;
  }

  userHasRights(): boolean {
    return Object.keys(this.rightsManagementService.getUserRights()).length > 0 || this.authenticationService.userIsAdmin;
  }
}
